import { IPubSubService } from '@vivli/shared/infrastructure/interface';
import { ClientOptions, Realtime } from 'ably';
import { LoggerService } from '@vivli/shared/infrastructure/service';

export class PubSubService implements IPubSubService {
    private ably: Realtime;
    private env: string;
    private apiBase: string;
    private getAuthToken: () => Promise<string>;

    public constructor(envName: string, apiBase: string, getAuthToken: () => Promise<string>) {
        this.apiBase = apiBase;
        this.env = envName;
        this.getAuthToken = getAuthToken;
    }

    // DEV-1103: the token endpoint requires a signed-in user. Ably calls this on the first connect and on
    // every automatic renewal, so each request carries a freshly acquired bearer token.
    private requestAblyToken: ClientOptions['authCallback'] = (_tokenParams, callback) => {
        this.getAuthToken()
            .then(async (authToken) => {
                if (!authToken) {
                    callback('No signed-in user, so no real-time token can be requested.', null);
                    return;
                }

                const response = await fetch(`${this.apiBase}api/VivliConfig/pubsub/auth/VivliPlatform`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                if (!response.ok) {
                    callback(`Real-time token request failed with status ${response.status}.`, null);
                    return;
                }

                callback(null, await response.json());
            })
            .catch((error) => {
                LoggerService.error('Failed to request a real-time token.', error);
                callback(error?.message || 'Failed to request a real-time token.', null);
            });
    };

    private initializeIfNotYetDone = () => {
        if (!this.ably) {
            this.ably = new Realtime({ authCallback: this.requestAblyToken });
            // Connect to Ably
            this.ably.connection.once('connected', () => {
                LoggerService.info('Connected to Ably!');
            });
        }
    };
    public SubscribeToChannel = (channelName: string, callback: (message: any) => void) => {
        const channel = this.InitializeChannel(channelName);
        this.logAttachFailure(channel.name, channel.subscribe(callback));
    };

    public UnsubscribeFromChannel = (channelName: string) => {
        const channel = this.InitializeChannel(channelName);

        // unsubscribe from the channel
        channel.unsubscribe();
    };

    public SubscribeToEvent = (channelName: string, eventName: string, callback: (message: any) => void) => {
        const channel = this.InitializeChannel(channelName);
        this.logAttachFailure(channel.name, channel.subscribe(eventName, callback));
    };

    // DEV-1103: ably-js queues a channel attach until the connection is up, so a subscribe made while
    // connecting or disconnected (for example while a failed token request is retried) is kept rather
    // than dropped. An attach that is refused, such as a capability denial, is logged instead of
    // surfacing as an unhandled promise rejection.
    private logAttachFailure = (channelName: string, attach: Promise<unknown>) => {
        attach.catch((error) => {
            LoggerService.error(`Ably could not attach to channel ${channelName}.`, error);
        });
    };

    public CloseConnection = () => {
        if (!this.ably?.connection) return;
        // Register connection close callback
        this.ably.connection.on('closed', () => {
            LoggerService.info('Closed the connection to Ably.');
        });

        // Close the connection without waiting to receive messages
        this.ably.connection.close();
    };

    private InitializeChannel = (channelName: string) => {
        this.initializeIfNotYetDone();
        channelName = this.PrependChannelName(channelName);
        // Get the channel to connect to
        return this.ably.channels.get(channelName);
    };

    private PrependChannelName = (channelName: string): string => {
        if (channelName.startsWith(`${this.env}:`)) return channelName;
        return `${this.env}:${channelName}`;
    };
}
